import { ref, nextTick, watch } from 'vue';
/**
 * Calculates the body height for the data grid, based on the space left on the screen.
 */
export function useCalculateBodyHeight(tableWrapper, props, topBar, footer, options, filterButtons, tableItems) {
    const windowHeight = ref(window.innerHeight);
    const bodyHeight = ref(getDataGridBodyHeight());
    function getDataGridBodyHeight() {
        const tableWrapperRef = tableWrapper.value;
        const topBarRef = topBar.value?.filterBarRef;
        const filterBarContentRef = topBar.value?.filterBarContent;
        const footerRef = footer.value;
        const items = tableItems.value || [];
        const itemCount = items.length;
        const itemsPerPage = options.itemsPerPage;
        const threshold = Math.min(itemsPerPage || 10, 10);
        // Not yet loaded. Return default
        if (!tableWrapperRef || itemCount < threshold) {
            return undefined;
        }
        // negative values can happen when the page is already scrolled
        const offset = Math.max(tableWrapperRef.getClientRects()[0].top, 0);
        const tableElement = tableWrapperRef.querySelector('table');
        const tableHeight = (tableElement ? tableElement.getBoundingClientRect().height : 0) + 15;
        // TopBar may have a lot of stuff, but most of them already calculated by offset. 10x2, 3x2 - paddings to not overflow
        const topBarHeight = (topBarRef?.clientHeight ?? 0) + (filterBarContentRef?.clientHeight ?? 0) + 26;
        const footerHeight = footerRef?.$el?.clientHeight ?? footerRef?.clientHeight ?? 0;
        const paddingBottom = 40;
        const minHeight = props.minBodyHeight ?? 450;
        const availableHeight = windowHeight.value - offset - topBarHeight - footerHeight - paddingBottom;
        let height = props.maxBodyHeight ?? Math.max(availableHeight, minHeight);
        // If original height is too small (for example, just 5 elements, but page is quite big) we don't have to set it, so leave as undefined
        height = height > tableHeight && tableHeight > 0 ? undefined : height;
        return height === undefined ? undefined : `${height}px`;
    }
    function recalculateBodyHeight() {
        bodyHeight.value = getDataGridBodyHeight();
    }
    watch(tableWrapper, (newValue) => {
        if (newValue) {
            recalculateBodyHeight();
            window.addEventListener('resize', function (_) {
                windowHeight.value = window.innerHeight;
                recalculateBodyHeight();
            }, true);
        }
    });
    watch(() => props.isLoading, async () => {
        await nextTick();
        recalculateBodyHeight();
    });
    watch(filterButtons, async (newVal) => {
        // when no are visible, recalculated anyway
        if (newVal.some((f) => f?.visible)) {
            await nextTick();
            recalculateBodyHeight();
        }
    }, { deep: true });
    watch(() => props.items, () => {
        recalculateBodyHeight();
    }, { deep: true });
    watch(() => tableItems, () => {
        recalculateBodyHeight();
    }, { deep: true });
    return { bodyHeight };
}
